class BroadcastService {
  constructor(channelName = "default-channel", userSpecific = false) {
    this.baseChannelName = channelName;
    this.userSpecific = userSpecific;
    this.channel = null;
    this.listeners = new Map();
    this.currentUserId = null;
    this._boundStorageHandler = null;
    if (this.userSpecific && typeof window !== "undefined") {
      this._boundStorageHandler = (e) => {
        if (e.key === "userId") {
          this.refreshChannel();
        }
      };
      window.addEventListener("storage", this._boundStorageHandler);
    }
    this.init();
  }

  init() {
    if (typeof BroadcastChannel !== "undefined") {
      const finalChannelName = this.getFinalChannelName();
      if (finalChannelName) {
        this.channel = new BroadcastChannel(finalChannelName);
        this.channel.onmessage = (event) => {
          const { type, data, isEvent } = event.data;
          if (this.listeners.has(type)) {
            this.listeners.get(type).forEach((callback) => {
              if (isEvent) {
                callback();
              } else {
                callback(data);
              }
            });
          }
        };
      }
    }
  }

  getFinalChannelName() {
    if (this.userSpecific) {
      if (
        typeof window === "undefined" ||
        typeof localStorage === "undefined"
      ) {
        console.warn(
          "BroadcastService: localStorage unavailable; cannot use user-specific channel",
        );
        this.currentUserId = null;
        return null;
      }
      const userId = localStorage.getItem("userId");
      this.currentUserId = userId || null;
      if (!userId) {
        console.warn("BroadcastService: userId not found in localStorage");
        return null;
      }
      return `${this.baseChannelName}-${userId}`;
    }
    return this.baseChannelName;
  }

  refreshChannel() {
    if (!this.userSpecific) return;
    const canUseLocalStorage =
      typeof window !== "undefined" && typeof localStorage !== "undefined";
    const newUserId = canUseLocalStorage
      ? localStorage.getItem("userId")
      : null;
    if (newUserId !== this.currentUserId) {
      if (this.channel) {
        try {
          this.channel.close();
        } catch (_) {
          // ignore
        }
        this.channel.onmessage = null;
        this.channel = null;
      }
      this.currentUserId = newUserId;
      this.init();
    }
  }

  send(type, data) {
    this.refreshChannel();
    if (this.channel) {
      this.channel.postMessage({ type, data, isEvent: false });
    }
  }

  trigger(eventType) {
    this.refreshChannel();
    if (this.channel) {
      this.channel.postMessage({ type: eventType, data: null, isEvent: true });
    }
  }

  listen(type, callback) {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set());
    }
    this.listeners.get(type).add(callback);
  }

  on(eventType, callback) {
    this.listen(eventType, callback);
  }

  off(type, callback) {
    if (this.listeners.has(type)) {
      this.listeners.get(type).delete(callback);
    }
  }

  unlisten(type, callback) {
    this.off(type, callback);
  }

  destroy() {
    if (this.channel) {
      try {
        this.channel.close();
      } catch (_) {
        // ignore
      }
    }
    if (this._boundStorageHandler && typeof window !== "undefined") {
      window.removeEventListener("storage", this._boundStorageHandler);
    }
    this.listeners.clear();
  }
}

export default BroadcastService;
